/* Dependencies */
// Helpers
import { TrapFocus } from '../../Utils/TrapFocus/TrapFocus';

// Models
import { VideoItem, ModalVideoBlockProps } from './ModalVideoBlock.model';

/**
 * ModalVideoBlock
 * @class
 */
class ModalVideoBlock {
  public parentElement: HTMLElement;
  public modalElement: HTMLElement;
  public triggerElement: HTMLElement;
  public closeButton: HTMLElement;
  public videoElement: VideoItem;
  private isOpen: boolean = false;
  private trapFocus: TrapFocus;

  constructor(props: ModalVideoBlockProps) {
    this.parentElement = props.parentElement;
    this.modalElement = props.modalElement;
    this.triggerElement = props.triggerElement;
    this.closeButton = props.closeButton;
    this.videoElement = props.videoElement;

    // Attach events
    this.bindEvents();

    this.trapFocus = new TrapFocus(this.modalElement, {
      escapeClose: true,
      escapeCloseCB: () => {
        this.closeModal();
      },
    });
  }

  /**
   * Open Modal
   */
  openModal(): void {
    // Ensure is not open.
    if (this.isOpen) return;

    // Remove hidden class for use
    this.modalElement.classList.remove('tw-hidden');

    // Set needed values to open
    this.isOpen = true;
    this.modalElement.setAttribute('aria-hidden', 'false');
    this.setVideo();
    this.trapFocus.attachTrapFocus();
  }

  /**
   * Close Modal
   */
  closeModal(): void {
    // Ensure is closed.
    if (!this.isOpen) return;

    // Remove trap focus
    this.trapFocus.dettachTrapFocus();

    // Set needed values to close
    this.isOpen = false;
    this.modalElement.setAttribute('aria-hidden', 'true');
    this.destroyVideo();
  }

  /**
   * Set Video by type into the modal
   */
  setVideo(): void {
    /* Create iframe and inject it */
    const iframe = document.createElement('iframe');
    iframe.setAttribute('src', this.videoElement.url);
    this.videoElement.iframeContainer.appendChild(iframe);

    // Reset the items that can be of focus
    this.trapFocus.defineFocusableElements();
    this.trapFocus.attachTrapFocus();
  }

  /**
   * Destroy Video
   */
  destroyVideo(): void {
    this.videoElement.iframeContainer.innerHTML = '';
  }

  /**
   * Binds the related events for the Modal.
   */
  bindEvents(): void {
    /* On click trigger element */
    this.triggerElement.addEventListener('click', (event) => {
      this.openModal();
    });
    /* On click close modal button */
    this.closeButton.addEventListener('click', (event) => {
      this.closeModal();
    });
  }
}

/**
 * Initiates the section.
 * @returns
 */
export const ModalVideoBlockInit = (): void => {
  // Detect if elements are present.
  if (!document.querySelector('[data-video-modal]')) return;

  // Get all section elements.
  const videoBlocksElements: HTMLElement[] = Array.from(
    document.querySelectorAll('[data-video-modal]')
  );

  // Loop and create section instances.
  videoBlocksElements.forEach((videoBlockElement) => {
    // Find the modal DOM Item
    var modalContainer = document.querySelector('[data-modal]') as HTMLElement;

    // Create ModalVideo.
    new ModalVideoBlock({
      parentElement: videoBlockElement,
      modalElement: modalContainer,
      triggerElement: document.querySelector(
        '[data-modal-trigger]'
      ) as HTMLElement,
      closeButton: modalContainer.querySelector('[data-close]') as HTMLElement,
      videoElement: {
        iframeContainer: modalContainer.querySelector(
          '[data-video]'
        ) as HTMLElement,
        url: videoBlockElement.getAttribute('data-src'),
      },
    });
  });
};
